import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateLine } from '@/api/mutations/useLineMutations';
import { ColorGrid } from '@/components/EntityCell';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { snackbarColor, textAtomTypeId } from '@/enums';
import { useColorMode, useRequiredAppSelector, useSnackbar } from '@/hooks';
import { type ILine, isApiError } from '@/types';
import { getNextCode, getNextColor } from '@/utils/lineHelpers';
import type { ModalDef } from './types';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

type Payload = { usedCodes: string[]; usedColors: string[] };
type Result = { line: ILine };

export const CreateLineModal: ModalDef<Payload, Result> = ({ open, onClose, resolve, setPending, usedCodes, usedColors }) => {
    const { t } = useTranslation();
    const isDarkMode = useColorMode();
    const project_id = useRequiredAppSelector('projectId');
    const showSnackbar = useSnackbar();
    const createLineMutation = useCreateLine();

    const [code, setCode] = useState(() => getNextCode(usedCodes.map(c => ({ code: c }))));
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [color, setColor] = useState(() => getNextColor(usedColors));
    const [submitting, setSubmitting] = useState(false);

    const takenLetters = useMemo(() => new Set(usedCodes.map(c => c.toUpperCase())), [usedCodes]);

    const canSubmit = title.trim().length > 0 && !submitting;

    const inputLabelSx = {
        position: 'static' as const,
        transform: 'none',
        m: 0,
        mb: 0.5,
        color: (theme: { custom: { c45: string } }) => theme.custom.c45,
        fontSize: 12,
        lineHeight: '18px',
        '&.Mui-focused': { color: (theme: { custom: { c45: string } }) => theme.custom.c45 },
    };
    const inputSx = {
        height: 40,
        color: (theme: { custom: { c1: string; c18: string } }) => (isDarkMode ? theme.custom.c1 : theme.custom.c18),
        backgroundColor: (theme: { custom: { c61: string } }) => theme.custom.c61,
        borderRadius: '8px',
    };
    const inputSxMultiline = {
        ...inputSx,
        height: 'auto',
        p: 1.5,
    };
    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: 'none' },
        },
    };

    const handleCreate = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setPending(true);
        try {
            const response = await createLineMutation.mutateAsync({
                project_id,
                title: title.trim(),
                color,
                code,
                text_atoms: description.trim()
                    ? [{ text_atom_type_id: textAtomTypeId.line_logline, content: description.trim() }]
                    : undefined,
            });
            resolve({ line: response.data });
            onClose();
        } catch (error) {
            if (isApiError(error)) {
                showSnackbar({
                    open: true,
                    message: error.data.error_codes ? t(`error_codes.${error.data.error_codes[0]}`) : error.data.message,
                    severity: snackbarColor.error,
                });
            }
            setSubmitting(false);
            setPending(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3,
                    borderRadius: '12px',
                    backgroundColor: theme => (isDarkMode ? theme.custom.c37 : theme.custom.c1),
                    color: isDarkMode ? theme => theme.custom.c1 : undefined,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: theme => theme.custom.c69,
                    fontSize: 24,
                    lineHeight: 1,
                    fontWeight: 500,
                    p: 0,
                }}
            >
                {t('line.create')}
                <IconButton onClick={onClose} disabled={submitting} sx={{ p: 0, color: theme => theme.custom.c45 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'visible' }}>
                <div className="flex gap-3">
                    <TextField
                        select
                        label={t('line.letter')}
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        variant="outlined"
                        margin="none"
                        sx={{ minWidth: 110, ...fieldSx }}
                        disabled={submitting}
                        InputLabelProps={{ shrink: true, sx: inputLabelSx }}
                        InputProps={{ sx: inputSx }}
                    >
                        {LETTERS.map(letter => (
                            <MenuItem key={letter} value={letter} disabled={letter !== code && takenLetters.has(letter)}>
                                {letter}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label={t('line.line-name')}
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        variant="outlined"
                        margin="none"
                        fullWidth
                        autoFocus
                        disabled={submitting}
                        inputProps={{ maxLength: 100 }}
                        InputLabelProps={{ shrink: true, sx: inputLabelSx }}
                        InputProps={{ sx: inputSx }}
                        sx={fieldSx}
                    />
                </div>
                <TextField
                    label={t('line.description')}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    variant="outlined"
                    margin="none"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={submitting}
                    inputProps={{ maxLength: 300 }}
                    InputLabelProps={{ shrink: true, sx: inputLabelSx }}
                    InputProps={{ sx: inputSxMultiline }}
                    sx={fieldSx}
                />
                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-c45">{t('lines.color-label')}</span>
                    <ColorGrid value={color} onChange={setColor} />
                </div>
            </DialogContent>
            <DialogActions sx={{ p: 0, flexDirection: 'column', gap: 0.5, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
                <Button onClick={handleCreate} variant="contained" disabled={!canSubmit} className="w-full rounded-lg">
                    {submitting ? <Spinner size={20} /> : t('line.create')}
                </Button>
                <Button onClick={onClose} variant="outlined" disabled={submitting} className="w-full rounded-lg dark:text-c4">
                    {t('cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
